import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  input,
  output,
} from '@angular/core';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ProductKeys } from '@config/keys/product.keys';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import {
  ContentBuilderColorValue,
  IContentBuilderFieldColor,
} from '@core/models/content-builder-fields.model';
import { DoodProductModel, IProductLabel } from '@core/models/product.model';
import { ProductTrackerService } from '@core/services/app/product-tracker.service';
import { ProductService } from '@core/services/product/product.service';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { combineLatest } from 'rxjs';

import { hasDiscountPromotionLabel } from '@core/services/eligible-promotions-labels/has-discount-promotion-label.util';
import { CartItemKeys } from '@config/keys/cart.keys';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { DiscountPercentPipe } from '@core/pipes/discount-percent.pipe';
import { FirstLinePipe } from '@core/pipes/first-line.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { ProductColorOverrides } from '@shared/interfaces/product-category-color-overrides.interface';
import { ProductPushV2AtomHoverColors } from '@shared/interfaces/product-hover-colors.interface';
import { VisibleLabelsPipe } from '@shared/pipes/visible-product-labels/visible-product-labels.pipe';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
import { InterfaceToInputs } from '@shared/utils/types/interface-to-inputs';
import { PriceAtomComponent } from '../price-atom/price-atom.component';
import { TagAtomComponent } from '../tag-atom/tag-atom.component';
import { CartStoreSelector } from '@common/selectors/cart.selector';

@Component({
  selector: 'app-product-push-v2-atom',
  templateUrl: './product-push-v2-atom.component.html',
  styleUrls: ['./product-push-v2-atom.component.scss'],
  imports: [
    ContentBuilderColorPipe,
    PriceAtomComponent,
    VisibleLabelsPipe,
    TagAtomComponent,
    TranslatePipe,
    DiscountPercentPipe,
    FirstLinePipe,
  ],
  host: {
    '[class.is-last-mobile-card]': 'isLastMobileCard()',
    '[style.--hover-bg-color]': 'hoverBackgroundColorValue()',
    '[style.--hover-name-color]': 'hoverNameColorValue()',
    '[style.--hover-description-color]': 'hoverDescriptionColorValue()',
    '[style.--hover-price-color]': 'hoverPriceColorValue()',
    '[style.--product-name-color]': 'productNameColorValue()',
    '[style.--product-description-color]': 'productDescriptionColorValue()',
    '[style.--product-price-color]': 'productPriceColorValue()',
    '[style.--product-image-placeholder-color]': 'productImagePlaceholderColorValue()',
    '[style.--product-border-color]': 'productBorderColorValue()',
    '[style.--product-background-color]': 'productBackgroundColorValue()',
  },
})
export class ProductPushV2AtomComponent
  implements OnInit, OnChanges, InterfaceToInputs<ProductColorOverrides, { required: false }>
{
  protected readonly defaultProductNameColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Hex,
  };

  protected readonly defaultProductDescriptionColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Hex,
  };

  protected readonly defaultProductPriceColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultProductImagePlaceholderColor: IContentBuilderFieldColor = {
    value: 'neutral-50',
    type: ColorFieldTypesValues.Hex,
  };

  protected readonly defaultProductBorderColor: IContentBuilderFieldColor = {
    value: 'neutral-100',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultProductBackgroundColor: IContentBuilderFieldColor = {
    value: 'transparent',
    type: ColorFieldTypesValues.Hex,
  };

  protected readonly defaultHoverBackgroundColor: IContentBuilderFieldColor = {
    value: 'neutral-50',
    type: ColorFieldTypesValues.Palette,
  };

  readonly displayContext = input('');
  readonly product = input.required<DoodProductModel>();
  readonly isLastMobileCard = input(false);
  readonly isOrderingAllowed = input(true);
  readonly productNameColor = input<ContentBuilderColorValue | undefined>(
    this.defaultProductNameColor,
  );
  readonly productDescriptionColor = input<ContentBuilderColorValue | undefined>(
    this.defaultProductDescriptionColor,
  );
  readonly productPriceColor = input<ContentBuilderColorValue | undefined>(
    this.defaultProductPriceColor,
  );
  readonly productImagePlaceholderColor = input<ContentBuilderColorValue | undefined>(
    this.defaultProductImagePlaceholderColor,
  );
  readonly productHoverColors = input<ProductPushV2AtomHoverColors | undefined>(undefined);
  readonly productBorderColor = input<ContentBuilderColorValue | undefined>();
  readonly productBackgroundColor = input<ContentBuilderColorValue | undefined>();
  readonly tagColors = input.required<ContentBuilderFieldColors>();
  readonly buttonClick = output<DoodProductModel>();
  readonly quantityInCart = computed(() => {
    const productId = this.product()[ProductKeys.Id];
    if (!productId) {
      return 0;
    }
    const productIdAsString = String(productId);

    return (
      this.cartSelector
        .selectActive()
        ?.products?.filter(item => String(item[CartItemKeys.ItemId]) === productIdAsString)
        .reduce((acc, item) => acc + (item[CartItemKeys.Quantity] ?? 0), 0) ?? 0
    );
  });

  // Computed signals for product colors
  protected readonly productNameColorValue = computed(() =>
    fromContentBuilderColor(this.productNameColor(), this.defaultProductNameColor),
  );

  protected readonly productDescriptionColorValue = computed(() =>
    fromContentBuilderColor(this.productDescriptionColor(), this.defaultProductDescriptionColor),
  );

  protected readonly productPriceColorValue = computed(() =>
    fromContentBuilderColor(this.productPriceColor(), this.defaultProductPriceColor),
  );

  protected readonly productImagePlaceholderColorValue = computed(() =>
    fromContentBuilderColor(
      this.productImagePlaceholderColor(),
      this.defaultProductImagePlaceholderColor,
    ),
  );

  // Computed signals for hover colors
  protected readonly hoverBackgroundColorValue = computed(() =>
    fromContentBuilderColor(
      this.productHoverColors()?.backgroundColor,
      this.defaultHoverBackgroundColor,
    ),
  );

  protected readonly hoverNameColorValue = computed(() =>
    fromContentBuilderColor(
      this.productHoverColors()?.nameColor,
      this.productNameColor() || this.defaultProductNameColor,
    ),
  );

  protected readonly hoverDescriptionColorValue = computed(() =>
    fromContentBuilderColor(
      this.productHoverColors()?.descriptionColor,
      this.productDescriptionColor() || this.defaultProductDescriptionColor,
    ),
  );

  protected readonly hoverPriceColorValue = computed(() =>
    fromContentBuilderColor(
      this.productHoverColors()?.priceColor,
      this.productPriceColor() || this.defaultProductPriceColor,
    ),
  );

  protected readonly productBorderColorValue = computed(() =>
    fromContentBuilderColor(this.productBorderColor(), this.defaultProductBorderColor),
  );

  protected readonly productBackgroundColorValue = computed(() =>
    fromContentBuilderColor(this.productBackgroundColor(), this.defaultProductBackgroundColor),
  );

  get productLabels(): IProductLabel[] {
    return this.product()[ProductKeys.Labels] ?? [];
  }
  protected readonly _hasDiscountPromotionLabel = computed(() =>
    hasDiscountPromotionLabel(this.product()),
  );

  isAvailable!: boolean;

  private readonly filterColors$ =
    this.marketplaceSelector.selectMarketPlaceProductLabelsFilterColors;

  protected readonly vm$ = combineLatest({
    filterColors: this.filterColors$,
  });

  constructor(
    private readonly productService: ProductService,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly productTrackerService: ProductTrackerService,
    private readonly cartSelector: CartStoreSelector,
  ) {}

  ngOnInit(): void {
    this.productTrackerService.trackProductDisplayed(this.product(), this.displayContext());
  }

  public get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  goToProduct(product: DoodProductModel): void {
    if (!this.isAvailable) {
      return;
    }
    this.buttonClick.emit(product);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product) {
      this.checkIsAvailable();
    }
  }

  checkIsAvailable(): void {
    this.isAvailable = this.productService.isAvailable(this.product());
  }

  displayDiscount(product: DoodProductModel): boolean {
    const discount = product[ProductKeys.Discount];
    return (discount && discount > 0) === true;
  }

  forceType(data: any): any {
    return data as any;
  }
}
